import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";

interface NextivaConfig {
  configured: boolean;
  id?: number;
  displayName?: string;
  accountId?: string;
  enableScreenPop?: boolean;
  enableCallLogging?: boolean;
  enableClickToCall?: boolean;
  createdAt?: string;
}

type Tab = "overview" | "config" | "activity";

export default function Nextiva() {
  const api = useApi();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");
  const [config, setConfig] = useState<NextivaConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState({
    apiKey: "", accountId: "", webhookSecret: "", displayName: "",
    enableScreenPop: true, enableCallLogging: false, enableClickToCall: true
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("Nextiva");
      if (resp.ok) setConfig(await resp.json());
    } catch { setConfig(null); } finally { setLoading(false); }
  }, [api.messaging]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setSaveError(null);
    try {
      const resp = await api.messaging.post("Nextiva", form);
      if (resp.ok) { await load(); setTab("overview"); }
      else setSaveError("Failed to save. Please check your settings.");
    } catch { setSaveError("Network error. Please try again."); } finally { setSaving(false); }
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "config", label: config?.configured ? "Settings" : "Connect" },
    { key: "activity", label: "Activity" }
  ];

  if (loading) return <div className="flex items-center justify-center p-12"><LoadingSpinner /></div>;

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center text-2xl">📞</div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Nextiva</h1>
          <p className="text-sm text-slate-500">VoIP CTI — Screen Pop + Call Logging</p>
        </div>
        {config?.configured && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
            ● Connected
          </span>
        )}
      </div>

      {/* API pending notice */}
      <div className="mb-5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
        <strong>Developer API pending.</strong> Nextiva primarily exposes call events via their JavaScript SDK.
        Contact Nextiva developer support to request webhook documentation for server-side call event integration.
        Screen pop works automatically once wired — no other changes needed.
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6 gap-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          {[
            {
              icon: "📲", title: "Inbound call screen pop",
              desc: "When a call comes in, Velaro automatically looks up the caller in VAST (if connected) and shows the customer name, vehicle, and service history in the agent sidebar — before they even answer."
            },
            {
              icon: "📋", title: "Call log sync",
              desc: "When a call ends, Velaro creates a call activity on the VAST customer record. Requires VAST Retail integration to also be configured."
            },
            {
              icon: "📱", title: "Click-to-call",
              desc: "Agents can initiate a call to a customer directly from the sidebar — opens the Nextiva softphone or desk phone."
            }
          ].map(item => (
            <div key={item.title} className="border border-slate-200 rounded-lg p-4 flex gap-3">
              <span className="text-2xl">{item.icon}</span>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "config" && (
        <div className="max-w-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">API Key <span className="text-red-500">*</span></label>
            <input type="password" value={form.apiKey} onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))}
              placeholder="From Nextiva developer portal"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Account ID</label>
            <input type="text" value={form.accountId} onChange={e => setForm(f => ({ ...f, accountId: e.target.value }))}
              placeholder="Nextiva account/tenant ID"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Webhook Secret</label>
            <input type="password" value={form.webhookSecret} onChange={e => setForm(f => ({ ...f, webhookSecret: e.target.value }))}
              placeholder="For HMAC signature validation (if Nextiva supports it)"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
            <input type="text" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
              placeholder="e.g. Main Office Nextiva"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="space-y-2">
            {[
              { key: "enableScreenPop", label: "Enable screen pop on inbound calls" },
              { key: "enableClickToCall", label: "Enable click-to-call from agent sidebar" },
              { key: "enableCallLogging", label: "Log call notes to VAST on call end (requires VAST integration)" }
            ].map(opt => (
              <div key={opt.key} className="flex items-center gap-2">
                <input type="checkbox" id={opt.key} checked={(form as any)[opt.key]}
                  onChange={e => setForm(f => ({ ...f, [opt.key]: e.target.checked }))}
                  className="rounded border-slate-300" />
                <label htmlFor={opt.key} className="text-sm text-slate-700">{opt.label}</label>
              </div>
            ))}
          </div>
          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
          <button onClick={save} disabled={saving || !form.apiKey}
            className="px-4 py-2 bg-[var(--color-primary)] text-white text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 transition-colors">
            {saving ? "Saving…" : config?.configured ? "Update" : "Save"}
          </button>
        </div>
      )}

      {tab === "activity" && <IntegrationActivityLog integrationSlug="Nextiva" />}
    </div>
  );
}

export function BackToIntegrationLandingNextiva() {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate("/integrations")}
      className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
      ← Integrations
    </button>
  );
}
